const HISTORICAL_ANALYTICS_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics/historical';
const ANALYTICS_API_KEY = import.meta.env.VITE_ANALYTICS_API_KEY;

export interface HistoricalAnalyticsRequest {
  metric: string;
  periods: string[];  // Min 2 quarters
  filters?: {
    category_id?: string[];
    employees?: string[];
    [key: string]: any;
  };
}

export interface HistoricalPeriodData {
  avg: number;
  total: number;
  count: number;
  median: number;
  min: number;
  max: number;
}

export interface HistoricalAnalyticsResponse {
  metric: string;
  periods: string[];
  overall: Record<string, HistoricalPeriodData>;
  metadata: {
    overlapping_companies: number;
    total_companies_per_period: Record<string, number>;
    execution_time_ms: number;
    generated_at: string;
  };
}

/**
 * Fetches historical analytics data for a given metric across multiple periods.
 * Only includes companies (slugs) that exist in ALL selected periods.
 */
export async function fetchHistoricalAnalytics(
  request: HistoricalAnalyticsRequest
): Promise<HistoricalAnalyticsResponse> {
  // Check if API key is configured
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  try {
    const response = await fetch(HISTORICAL_ANALYTICS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch historical analytics';
      try {
        const errorData = await response.json();
        console.error('📊 [historicalAnalyticsAPI] Error response:', errorData);
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data: HistoricalAnalyticsResponse = await response.json();

    return data;
  } catch (error) {
    console.error('📊 [historicalAnalyticsAPI] Fetch error:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}
